import type { FC, FocusEvent, ChangeEvent } from 'react'
import { useState, useEffect, useRef } from 'react'
import { type FieldInputProps, useField, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  type TextFieldProps,
  Select,
  MenuItem,
  type SelectChangeEvent,
  selectClasses,
  outlinedInputClasses,
  Box,
} from '@mui/material'
import { type SvgIconProps, styled } from '@dhl/theme'
import { FlagNL, FlagBE, FlagSE } from '@dhl/flags'

import { ErrorInputAdornment } from '@dhl/inputs'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'

const CountryDialCodeSelect = styled(Select)(({ theme }) => ({
  backgroundColor: 'transparent',
  minWidth: 100,
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
  },
  [`& .${selectClasses.select}`]: {
    display: 'flex',
    maxHeight: 18,
  },
  [`& .${selectClasses.icon}.${selectClasses.disabled}`]: {
    display: 'none',
  },
}))

const PhoneNumberInput = styled(TextField)(({ theme }) => ({
  left: -1,
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
  },
}))

const PhoneNumberLabel = styled(InputLabel)({
  top: 8,
  left: 10,
})

const flagStyles = {
  position: 'relative',
  top: -2,
  width: 24,
  height: 20,
  marginRight: 0.5,
}

//
// Default list of available countries/dialcodes. This list can be expanded as needed.
//
export const defaultCountryDialCodes: CountryDialCodeList = {
  nl: {
    iso2: 'nl',
    dialCode: '+31',
    validationPattern: '(?<DialCode>\\+31)0?(?<PhoneNumber>\\d+)',
    mobileValidationPattern: '(?<DialCode>\\+31)0?(?<PhoneNumber>6\\d+)',
    minLength: 12,
    maxLength: 12,
    phoneNumberLength: 9,
    Flag: FlagNL,
  },
  be: {
    iso2: 'be',
    dialCode: '+32',
    validationPattern: '(?<DialCode>\\+32)(?<PhoneNumber>\\d+)',
    mobileValidationPattern: '(?<DialCode>\\+32)0?(?<PhoneNumber>4\\d+)',
    minLength: 12,
    maxLength: 12,
    phoneNumberLength: 9,
    Flag: FlagBE,
  },
  se: {
    iso2: 'se',
    dialCode: '+46',
    validationPattern: '(?<DialCode>\\+46)0?(?<PhoneNumber>\\d+)',
    mobileValidationPattern: '(?<DialCode>\\+46)0?(?<PhoneNumber>7\\d+)',
    minLength: 12,
    maxLength: 12,
    phoneNumberLength: 9,
    Flag: FlagSE,
  },
}

export type CountryDialCode = {
  iso2: string
  minLength: number
  maxLength: number
  phoneNumberLength: number
  dialCode: string
  validationPattern: string
  mobileValidationPattern?: string
  Flag: FC<SvgIconProps>
}

export type CountryDialCodeList = Record<string, CountryDialCode>

type Props = Omit<TextFieldProps, keyof FieldInputProps<string> | 'error' | 'inputRef'> & {
  name: string
  onDialCodeSelect: (countryDialCode: CountryDialCode) => void
  countryFilter: string[]
  countryDialCodeList?: CountryDialCodeList
  isMobile?: boolean
  testId?: string
}

type CountryResult = {
  countryDialCode: CountryDialCode
  phoneNumberWithoutDialCode: string
}

function determineCountry(
  value: string,
  countryDialCodeList: CountryDialCodeList,
  countryFilter: string[]
): CountryResult {
  let result: CountryResult = {
    countryDialCode: defaultCountryDialCodes[countryFilter[0]],
    phoneNumberWithoutDialCode: '',
  }
  Object.values(countryDialCodeList).find((countryDialCode) => {
    const regEx = new RegExp(countryDialCode.validationPattern)
    const match = value.match(regEx)
    if (match?.groups?.DialCode === countryDialCode.dialCode) {
      result = {
        countryDialCode,
        phoneNumberWithoutDialCode: match?.groups?.PhoneNumber ?? '',
      }
      return true
    }
    return false
  })

  return result
}

export const InternationalPhoneNumberInput: FC<Props> = ({
  name,
  onDialCodeSelect,
  countryFilter,
  countryDialCodeList = defaultCountryDialCodes,
  helperText,
  inputProps,
  isMobile,
  disabled,
  testId = 'international-phone-number',
  ...props
}) => {
  const { validateOnBlur, validateField } = useFormikContext()
  const [field, meta, helpers] = useField<string>(name)
  const { onChange, ...fieldProps } = field

  const countryDialCodesToShow = countryFilter
    ? Object.values(countryDialCodeList).filter((dialCode) => countryFilter.find((cf) => cf === dialCode.iso2))
    : Object.values(countryDialCodeList)

  const [countryDialCode, setCountryDialCode] = useState<CountryDialCode>(countryDialCodesToShow[0])
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (!countryDialCodeList) {
      return
    }
    const initialValue = determineCountry(field.value, countryDialCodeList, countryFilter)
    setCountryDialCode(initialValue.countryDialCode)
    setPhoneNumber(initialValue.phoneNumberWithoutDialCode)
    onDialCodeSelect(initialValue.countryDialCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryDialCodeList, meta.initialValue])

  useFocusOnFirstError(name, inputRef, inputRef)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const regEx = new RegExp(
      isMobile && countryDialCode.mobileValidationPattern
        ? countryDialCode.mobileValidationPattern
        : countryDialCode.validationPattern
    )
    const fullPhoneNumber = `${countryDialCode.dialCode}${phoneNumber.replace(/-/g, '')}`
    const match = fullPhoneNumber.match(regEx)
    const prunedPhoneNumber = match?.groups?.PhoneNumber ?? phoneNumber
    setPhoneNumber(prunedPhoneNumber)
    helpers.setTouched(true, false)
    helpers.setValue(`${countryDialCode.dialCode}${prunedPhoneNumber}`, validateOnBlur)
  }

  const onChangecountryDialCode = (e: SelectChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const countryDialCode = countryDialCodesToShow.find((c) => c.dialCode === e.target.value)!
    setCountryDialCode(countryDialCode)
    onDialCodeSelect(countryDialCode)
    requestAnimationFrame(() => validateField(name))
  }

  const onChangePhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
    const phoneNumberValue = e.currentTarget.value
    setPhoneNumber(phoneNumberValue)
    helpers.setValue(`${countryDialCode.dialCode}${phoneNumberValue}`)
  }

  return (
    <FormControl data-test={`${testId}-component`} fullWidth>
      <Box display="flex">
        <PhoneNumberLabel id={`${name}-country-code`}>
          {t(`internationalPhoneNumberInput.countryCodeLabel`, { defaultValue: '' })}
        </PhoneNumberLabel>
        <CountryDialCodeSelect
          labelId={`${name}-country-code`}
          data-test={`${testId}-select`}
          name="countryDialCode"
          variant="outlined"
          value={countryDialCode.dialCode}
          onChange={onChangecountryDialCode}
          error={!!meta.error && meta.touched}
          disabled={disabled || countryDialCodesToShow.length < 2}
        >
          {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
          {countryDialCodesToShow.map(({ iso2, Flag, dialCode }) => (
            <MenuItem key={iso2} value={dialCode} data-test="dialcode-option">
              <Flag sx={flagStyles} />
              {dialCode}
            </MenuItem>
          ))}
        </CountryDialCodeSelect>
        <PhoneNumberInput
          {...props}
          {...fieldProps}
          value={phoneNumber}
          variant="outlined"
          id={name}
          type="tel"
          label={
            isMobile
              ? t('phoneInput.internationalPhoneNumberInput.mobileNumberLabel', { defaultValue: '' })
              : t('phoneInput.internationalPhoneNumberInput.phoneNumberLabel', { defaultValue: '' })
          }
          data-test={`${testId}-input`}
          inputRef={inputRef}
          onBlur={onBlur}
          onChange={onChangePhoneNumber}
          error={!!meta.error && meta.touched}
          disabled={disabled}
          slotProps={{
            htmlInput: inputProps,
            input: {
              endAdornment: <ErrorInputAdornment visible={meta.touched && !!meta.error} />,
            },
          }}
        />
      </Box>
      <FormHelperText error={!!meta.error && meta.touched}>
        {meta.touched && meta.error ? meta.error : helperText}
      </FormHelperText>
    </FormControl>
  )
}

export const createInternationalPhoneNumberValidationSchema = (
  t: TFunction,
  countryDialCode: CountryDialCode,
  isMobile = false
): Yup.StringSchema<string> =>
  Yup.string()
    .required(
      t('phoneInput.internationalPhoneNumberInput.numberIsRequiredErrorMessage', {
        defaultValue: 'numberIsRequiredErrorMessage',
      })
    )
    .min(
      countryDialCode.minLength,
      t('phoneInput.internationalPhoneNumberInput.numberIsShortMessage', {
        min: countryDialCode.phoneNumberLength,
        defaultValue: 'numberIsShortMessage',
      })
    )
    .max(
      countryDialCode.maxLength,
      t('phoneInput.internationalPhoneNumberInput.numberIsLongMessage', {
        max: countryDialCode.phoneNumberLength,
        defaultValue: 'numberIsLongMessage',
      })
    )
    .matches(
      new RegExp(
        isMobile
          ? (countryDialCode.mobileValidationPattern ?? countryDialCode.validationPattern)
          : countryDialCode.validationPattern
      ),
      t('phoneInput.internationalPhoneNumberInput.numberInvalidFormatMessage', {
        defaultValue: 'numberInvalidFormatMessage',
      })
    )
